// ── Orçamento Tab ─────────────────────────────────────────────────────────────
import { getTrip }          from '@/store/tripState';
import { saveTrip }         from '@/services/sync.service';
import { toArr, fmtR,
         fmtMask, parseMask,
         avatarColor }      from '@/helpers';
import { COMMON_CURRENCIES } from '@/constants';
import type { Chart }       from 'chart.js';
import type { Expense }     from '@/types';

let barChart: Chart | null = null;
let pieChart: Chart | null = null;

export function renderBudget(): void {
  const trip       = getTrip();
  const primaryCur = (trip.currencies as string[] | undefined)?.[0] ?? 'BRL';

  const tArr = toArr(trip.transporte ?? {});
  const hArr = toArr(trip.hoteis    ?? {});
  const aArr = toArr(trip.agenda    ?? {}).filter(a => (a.preco ?? 0) > 0);
  const eArr = toArr(trip.expenses  ?? {}) as Expense[];

  // Structural totals (preco fields are implicitly in primary currency)
  const tTotal = tArr.reduce((s, t) => s + ((t as {preco?:number}).preco ?? 0), 0);
  const hTotal = hArr.reduce((s, h) => s + ((h as {preco?:number}).preco ?? 0), 0);
  const aTotal = aArr.reduce((s, a) => s + ((a as {preco?:number}).preco ?? 0), 0);

  // Expense totals split by currency
  const expByCur: Record<string, number> = {};
  eArr.forEach(e => { expByCur[e.currency] = (expByCur[e.currency] ?? 0) + e.amount; });
  const expPrimary = expByCur[primaryCur] ?? 0;
  const foreignExp = Object.entries(expByCur).filter(([cur]) => cur !== primaryCur);

  const total = tTotal + hTotal + aTotal + expPrimary;
  const limit = trip.budgetLimit ?? 0;
  const pct   = limit ? Math.min(100, Math.round(total / limit * 100)) : 0;

  // Per-person breakdown (structural + primary-currency expenses)
  const travelers = toArr(trip.travelers ?? {});
  const perPerson: Record<string, number> = {};
  travelers.forEach(tr => { perPerson[tr.id] = 0; });
  [...tArr, ...hArr, ...aArr].forEach(item => {
    const p = (item as {pagador?:string}).pagador;
    if (p && perPerson[p] !== undefined) perPerson[p] += (item as {preco?:number}).preco ?? 0;
  });
  eArr.filter(e => e.currency === primaryCur).forEach(e => {
    if (e.pagador && perPerson[e.pagador] !== undefined) perPerson[e.pagador] += e.amount;
  });

  const labels    = travelers.map(tr => tr.name);
  const vals      = travelers.map(tr => perPerson[tr.id] ?? 0);
  const colors    = travelers.map((_, i) => avatarColor(i));
  const curInfo   = COMMON_CURRENCIES.find(c => c.code === primaryCur);
  const curSymbol = curInfo?.symbol ?? primaryCur;

  if (barChart) { barChart.destroy(); barChart = null; }
  if (pieChart) { pieChart.destroy(); pieChart = null; }

  const barCtx = document.getElementById('chart-bar') as HTMLCanvasElement | null;
  const pieCtx = document.getElementById('chart-pie') as HTMLCanvasElement | null;

  if (barCtx && labels.length) {
    import('chart.js').then(({ Chart: ChartJS, registerables }) => {
      ChartJS.register(...registerables);
      barChart = new ChartJS(barCtx, {
        type: 'bar',
        data: { labels, datasets: [{ data: vals, backgroundColor: colors, borderRadius: 6, borderSkipped: false }] },
        options: { plugins: { legend: { display: false } }, scales: {
          y: { ticks: { callback: v => curSymbol + ' ' + Math.round(Number(v)).toLocaleString('pt-BR') } },
          x: { grid: { display: false } } }, responsive: true, maintainAspectRatio: true },
      });
      if (pieCtx) {
        pieChart = new ChartJS(pieCtx, {
          type: 'doughnut',
          data: { labels, datasets: [{ data: vals, backgroundColor: colors, borderWidth: 2, borderColor: 'var(--bg)' }] },
          options: { plugins: { legend: { position: 'bottom', labels: { font: { size: 11 }, boxWidth: 12 } } }, responsive: true, maintainAspectRatio: true, cutout: '55%' },
        });
      }
    });
  }

  const row = (label: string, value: string) =>
    `<div style="display:flex;justify-content:space-between;padding:7px 0;border-bottom:0.5px solid var(--border);font-size:13px">` +
    `<span style="color:var(--muted)">${label}</span><span style="font-weight:500">${value}</span></div>`;

  const foreignRows = foreignExp.map(([cur, amt]) => {
    const info = COMMON_CURRENCIES.find(c => c.code === cur);
    return row(`Gastos (${cur})`, `${info?.symbol ?? cur} ${amt.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
  }).join('');

  const bd = document.getElementById('budget-breakdown');
  if (bd) bd.innerHTML =
    row('Transporte', fmtR(tTotal)) +
    row('Hospedagem', fmtR(hTotal)) +
    row('Atrações / Agenda', fmtR(aTotal)) +
    (expPrimary > 0 ? row(`Gastos registrados (${primaryCur})`, fmtR(expPrimary)) : '') +
    foreignRows +
    travelers.map(tr => row(`Pago por ${tr.name}`, fmtR(perPerson[tr.id] ?? 0))).join('');

  const totEl = document.getElementById('budget-total');
  if (totEl) totEl.textContent = fmtR(total);

  const prog = document.getElementById('budget-prog') as HTMLElement | null;
  if (prog) {
    prog.style.width      = pct + '%';
    prog.style.background = pct > 80 ? 'var(--lux-red)' : pct > 60 ? 'var(--amber)' : 'var(--lux-blue)';
  }
  const pctEl = document.getElementById('budget-pct');
  if (pctEl) pctEl.textContent = pct ? pct + '%' : '—';
  const limLbl = document.getElementById('budget-lim-lbl');
  if (limLbl) limLbl.textContent = limit ? 'Limite: ' + fmtR(limit) : 'Sem limite definido';
  const inp = document.getElementById('budget-limit-input') as HTMLInputElement | null;
  if (inp && document.activeElement !== inp) inp.value = limit ? fmtMask(limit) : '';
}

export function saveBudgetLimit(): void {
  const v = parseMask((document.getElementById('budget-limit-input') as HTMLInputElement)?.value ?? '0');
  saveTrip('budgetLimit', v).catch(() => void 0);
}
